<template>
  <v-container fluid>
    <v-card style="border-radius: 20px;">
      <v-row>
        <div class="title-displays" style="padding-top: 15px; padding-left: 15px">
          Dashboard de Projetos
        </div>
        <v-spacer></v-spacer>
        <div class="d-flex align-center" style="gap: 16px; padding-top: 15px; padding-right: 15px">
          <v-chip color="primary" text-color="white" class="mr-4">
            <v-icon left>mdi-chart-line</v-icon>
            Visão Geral
          </v-chip>
        </div>
      </v-row>

      <!-- Filtros -->
      <v-row dense class="ma-2">
        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="selectedFranchisee"
            :items="franchiseeOptions"
            label="Filtrar por Franqueado"
            clearable
            prepend-icon="mdi-filter"
            @change="updateCharts"
            :search-input.sync="franchiseeSearch"
            no-data-text="Nenhum franqueado encontrado"
            placeholder="Digite para buscar..."
            item-text="text"
            item-value="value"
            return-object
            :loading="isLoading"
            :disabled="isLoading"
            hide-selected
            auto-select-first
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedPeriod"
            :items="periodOptions"
            label="Período"
            prepend-icon="mdi-calendar"
            @change="updateCharts"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedStep"
            :items="stepOptions"
            label="Filtrar por Etapa"
            clearable
            prepend-icon="mdi-filter-variant"
            @change="updateCharts"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-btn
            color="primary"
            @click="refreshData"
            :loading="isLoading"
            prepend-icon="mdi-refresh"
          >
            Atualizar
          </v-btn>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-row v-if="isLoading" class="ma-4">
        <v-col cols="12" class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            width="6"
          />
          <div class="mt-4 text-h6">Carregando dados...</div>
        </v-col>
      </v-row>

      <!-- Visão Geral -->
      <div v-if="!isLoading">
        <!-- KPIs Principais -->
        <v-row class="ma-2">
          <v-col cols="12" md="3">
            <v-card class="kpi-card" color="primary" dark>
              <v-card-text>
                <div class="d-flex align-center">
                  <v-icon size="40" class="mr-3">mdi-folder-multiple</v-icon>
                  <div>
                    <div class="text-h4 font-weight-bold">{{ totalProjects }}</div>
                    <div class="text-subtitle-1">Total de Projetos</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card class="kpi-card" color="success" dark>
              <v-card-text>
                <div class="d-flex align-center">
                  <v-icon size="40" class="mr-3">mdi-check-circle</v-icon>
                  <div>
                    <div class="text-h4 font-weight-bold">{{ completedProjects }}</div>
                    <div class="text-subtitle-1">Projetos Finalizados</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card class="kpi-card" color="warning" dark>
              <v-card-text>
                <div class="d-flex align-center">
                  <v-icon size="40" class="mr-3">mdi-clock-outline</v-icon>
                  <div>
                    <div class="text-h4 font-weight-bold">{{ inProgressProjects }}</div>
                    <div class="text-subtitle-1">Em Andamento</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card class="kpi-card" color="info" dark>
              <v-card-text>
                <div class="d-flex align-center">
                  <v-icon size="40" class="mr-3">mdi-account-group</v-icon>
                  <div>
                    <div class="text-h4 font-weight-bold">{{ activeFranchisees }}</div>
                    <div class="text-subtitle-1">Franqueados Ativos</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Gráficos Principais -->
        <v-row class="ma-2">
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>
                <v-icon class="mr-2">mdi-chart-pie</v-icon>
                Distribuição por Etapa
              </v-card-title>
              <v-card-text>
                <div ref="stepDistributionChart" style="height: 300px;"></div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>
                <v-icon class="mr-2">mdi-chart-bar</v-icon>
                Projetos por Franqueado
              </v-card-title>
              <v-card-text>
                <div ref="franchiseeProjectsChart" style="height: 300px;"></div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="ma-2">
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>
                <v-icon class="mr-2">mdi-chart-line</v-icon>
                Evolução Mensal
              </v-card-title>
              <v-card-text>
                <div ref="monthlyEvolutionChart" style="height: 300px;"></div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>
                <v-icon class="mr-2">mdi-chart-donut</v-icon>
                Status dos Projetos
              </v-card-title>
              <v-card-text>
                <div ref="statusDistributionChart" style="height: 300px;"></div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

    </v-card>
  </v-container>
</template>

<script>
import ProjectsDashboardScript from "./ProjectsDashboardScript.js";

export default {
  name: "ProjectsDashboard",
  extends: ProjectsDashboardScript,
  data() {
    return {
      ...ProjectsDashboardScript.data()
    }
  },
  methods: {
    ...ProjectsDashboardScript.methods
  }
};
</script>

<style scoped>
.title-displays {
  font-size: 25px;
  padding-top: 8px;
  padding-bottom: 8px;
}

.kpi-card {
  border-radius: 12px;
  transition: transform 0.2s;
}

.kpi-card:hover {
  transform: translateY(-2px);
}

.v-btn-toggle {
  border-radius: 8px;
  overflow: hidden;
}

.v-btn-toggle .v-btn {
  border: none !important;
  opacity: 0.7;
}

.v-btn-toggle .v-btn.v-btn--active {
  opacity: 1;
  background-color: rgba(var(--v-theme-primary), 0.1);
}
</style>
